import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import TextAreaField from '../../../components/ui/TextAreaField';
import ToggleSwitch from '../../../components/ui/ToggleSwitch';
import RichTextEditor from '../../../components/ui/RichTextEditor';
import { TEACHER_TRAINING_LOBBY_PATH } from '../../../config/contentRoutes';
import { FEATURE_TRAINING_INTERNAL_LINKS } from '../../../config/featureFlags';
import {
  addTrainingProgram,
  formatTrainingHostError,
  getTrainingHostByOwnerUid,
  isValidProgramSlug,
  normalizeProgramSlug,
} from '../../../utils/trainingHost';
import type { TrainingProgramLinkType, TrainingProgramStatus } from '../../../types/trainingHost';
import { DEFAULT_TRAINING_CONTENT_HTML } from '../../../utils/trainingHtml';

type RegisterLocationState = {
  title?: string;
  status?: TrainingProgramStatus;
};

const TrainingProgramRegister: React.FC = () => {
  const { mode: modeParam = 'editor' } = useParams<{ mode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const draft = (location.state as RegisterLocationState | null) ?? {};

  const isLinkMode = modeParam === 'link';

  const [hostId, setHostId] = useState<string | null>(null);
  const [hostSlug, setHostSlug] = useState('');
  const [title, setTitle] = useState(draft.title ?? '');
  const [description, setDescription] = useState('');
  const [programSlug, setProgramSlug] = useState('');
  const [contentHtml, setContentHtml] = useState(DEFAULT_TRAINING_CONTENT_HTML);
  const [linkPath, setLinkPath] = useState('');
  const [linkType, setLinkType] = useState<TrainingProgramLinkType>(
    FEATURE_TRAINING_INTERNAL_LINKS ? 'internal' : 'external'
  );
  const [status, setStatus] = useState<TrainingProgramStatus>(draft.status ?? 'upcoming');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;
    getTrainingHostByOwnerUid(currentUser.uid)
      .then((host) => {
        if (host) {
          setHostId(host.id);
          setHostSlug(host.publicSlug);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [currentUser?.uid]);

  useEffect(() => {
    if (modeParam === 'link' && !FEATURE_TRAINING_INTERNAL_LINKS) {
      toast.info('내부 페이지 연결은 사용하지 않습니다. 웹 에디터로 연수를 추가해 주세요.');
      navigate(TEACHER_TRAINING_LOBBY_PATH, { replace: true });
      return;
    }
    if (modeParam !== 'editor' && modeParam !== 'link') {
      navigate(TEACHER_TRAINING_LOBBY_PATH, { replace: true });
    }
  }, [modeParam, navigate]);

  const normalizedProgramSlug = normalizeProgramSlug(programSlug);

  const ensureHost = async (): Promise<string | null> => {
    if (!currentUser?.uid) return null;
    if (hostId) return hostId;

    toast.error('먼저 로비에서 공개 주소와 프로필을 저장해 주세요.');
    navigate(TEACHER_TRAINING_LOBBY_PATH);
    return null;
  };

  const handleRegister = async () => {
    if (!currentUser?.uid || !title.trim()) {
      toast.error('연수 제목을 입력해 주세요.');
      return;
    }

    const resolvedHostId = await ensureHost();
    if (!resolvedHostId) return;

    if (isLinkMode) {
      const trimmedPath = linkPath.trim();
      if (!trimmedPath) {
        toast.error('연결할 링크를 입력해 주세요.');
        return;
      }
      if (linkType === 'external' && !/^https?:\/\//i.test(trimmedPath)) {
        toast.error('외부 링크는 http:// 또는 https:// 로 시작해야 합니다.');
        return;
      }
      if (linkType === 'internal' && !trimmedPath.startsWith('/')) {
        toast.error('내부 경로는 / 로 시작해야 합니다.');
        return;
      }
    } else if (!isValidProgramSlug(normalizedProgramSlug)) {
      toast.error('연수 주소(slug) 형식을 확인해 주세요.');
      return;
    }

    setSubmitting(true);
    try {
      await addTrainingProgram(currentUser.uid, resolvedHostId, {
        title: title.trim(),
        description: description.trim() || undefined,
        status,
        date: date || undefined,
        category: category.trim() || undefined,
        isPublic,
        ...(isLinkMode
          ? {
              contentType: 'legacy' as const,
              path: linkPath.trim(),
              linkType,
            }
          : {
              contentType: 'editor' as const,
              programSlug: normalizedProgramSlug,
              contentHtml,
              linkType: 'internal' as const,
            }),
      });

      toast.success('연수가 등록되었습니다.');
      navigate(TEACHER_TRAINING_LOBBY_PATH, { replace: true });
    } catch (err) {
      console.error(err);
      toast.error(formatTrainingHostError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', fontFamily: 'var(--font-family-primary)' }}>
        <p style={{ color: 'var(--Color-text-normal)' }}>불러오는 중…</p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        padding: '32px 24px 48px',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outline" size="medium" onClick={() => navigate(TEACHER_TRAINING_LOBBY_PATH)}>
            ← 로비
          </Button>
          <Button variant="primary" size="medium" onClick={handleRegister} disabled={submitting || !hostId}>
            {submitting ? '등록 중…' : '등록'}
          </Button>
        </div>

        {!hostId && (
          <Card padding="16px 20px" borderRadius="12px" background="var(--Color-background-normal)" width="100%">
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
              연수를 등록하려면 먼저 로비에서 프로필과 공개 주소를 저장해 주세요.
            </p>
          </Card>
        )}

        <div>
          <div
            style={{
              display: 'inline-block',
              marginBottom: '8px',
              padding: '4px 10px',
              borderRadius: '999px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--Color-fill-brand-color)',
              background: 'var(--Color-fill-hover)',
            }}
          >
            {isLinkMode ? '링크 등록' : '에디터 등록'}
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: '26px', fontWeight: 700, color: 'var(--Color-text-strong)' }}>
            {isLinkMode ? '연수 링크 등록' : '연수 페이지 등록'}
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
            {isLinkMode
              ? '로비에 표시될 연수 정보와 연결 링크를 입력한 뒤 등록하세요.'
              : '로비에 표시될 연수 정보와 본문을 작성한 뒤 등록하세요.'}
          </p>
        </div>

        <Card padding="20px" borderRadius="16px" background="var(--Color-background-normal)" width="100%" style={{ boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <InputField label="연수 제목" value={title} onChange={setTitle} width="100%" />
            <TextAreaField label="요약 (로비 목록용)" value={description} onChange={setDescription} />
            {!isLinkMode && (
              <div>
                <InputField
                  label="연수 주소 (slug)"
                  value={programSlug}
                  onChange={setProgramSlug}
                  placeholder="3d-printing-2026"
                  width="100%"
                />
                {hostSlug && (
                  <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--Color-text-normal)' }}>
                    공개 URL: /teacher-training/h/{hostSlug}/p/{normalizedProgramSlug || 'slug'}
                  </p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <InputField label="일정" value={date} onChange={setDate} type="date" width="200px" />
              <InputField label="분야" value={category} onChange={setCategory} width="200px" placeholder="AI, 3D프린팅" />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>상태</span>
              {(['upcoming', 'active', 'completed'] as TrainingProgramStatus[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: status === s ? '2px solid var(--Color-fill-brand-color)' : '1px solid var(--Color-line-normal)',
                    background: status === s ? 'var(--Color-fill-hover)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  {s === 'upcoming' ? '예정' : s === 'active' ? '진행 중' : '완료'}
                </button>
              ))}
            </div>
            <ToggleSwitch label="공개" checked={isPublic} onChange={setIsPublic} />
          </div>
        </Card>

        {isLinkMode ? (
          <Card padding="20px" borderRadius="16px" background="var(--Color-background-normal)" width="100%" style={{ boxSizing: 'border-box' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
              연결 링크
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {(FEATURE_TRAINING_INTERNAL_LINKS
                ? (['internal', 'external'] as TrainingProgramLinkType[])
                : (['external'] as TrainingProgramLinkType[])
              ).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setLinkType(type)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: linkType === type ? '2px solid var(--Color-fill-brand-color)' : '1px solid var(--Color-line-normal)',
                    background: linkType === type ? 'var(--Color-fill-hover)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {type === 'internal' ? '내부 페이지' : '외부 사이트'}
                </button>
              ))}
            </div>
            <InputField
              label={linkType === 'internal' ? '내부 경로' : '외부 URL'}
              value={linkPath}
              onChange={setLinkPath}
              placeholder={linkType === 'internal' ? '/teacher-training/AIEdutech' : 'https://example.com'}
              width="100%"
            />
          </Card>
        ) : (
          <Card
            padding="20px"
            borderRadius="16px"
            background="var(--Color-background-normal)"
            width="100%"
            alignItems="stretch"
            style={{ boxSizing: 'border-box' }}
          >
            <h2 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
              본문
            </h2>
            <RichTextEditor
              value={contentHtml}
              onChange={setContentHtml}
              minHeight="480px"
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrainingProgramRegister;
